/**
 * Per-row change history, from CFG_audit.
 *
 * The row stamp answers "who last touched this" at a glance; this answers "who
 * changed it before that, from what, to what" — the question that gets asked
 * months later. Rendered as a field-level diff rather than two JSON blobs,
 * because nobody reads two JSON blobs.
 */

import type { AuditEntry, ConfigTypeSchema, FieldSchema } from '../api/types'
import { formatTimestamp, shortenUpn } from '../lib/format'
import { CellValue } from '../fields/CellValue'
import { EmptyState, Spinner } from './feedback'

interface Change {
  field: string
  label: string
  schema: FieldSchema | undefined
  from: unknown
  to: unknown
}

function diff(schema: ConfigTypeSchema, entry: AuditEntry): Change[] {
  const before = entry.old_value ?? {}
  const after = entry.new_value ?? {}
  const names = new Set([...Object.keys(before), ...Object.keys(after)])
  const changes: Change[] = []

  for (const name of names) {
    // The stamps are on every row by construction; listing them as changes
    // would bury the one field the person actually altered.
    if (name === 'updated_at' || name === 'updated_by') continue
    if (name === schema.key_field.name) continue
    if (JSON.stringify(before[name]) === JSON.stringify(after[name])) continue
    const field = schema.fields.find((f) => f.name === name)
    changes.push({
      field: name,
      label: field?.label ?? name,
      schema: field,
      from: before[name],
      to: after[name],
    })
  }
  return changes
}

function entryVerb(entry: AuditEntry): string {
  if (!entry.old_value) return 'created'
  if (!entry.new_value) return 'deleted'
  return 'changed'
}

export function HistoryPanel({
  schema,
  entries,
  isLoading,
}: {
  schema: ConfigTypeSchema
  entries: AuditEntry[] | undefined
  isLoading: boolean
}) {
  if (isLoading) return <Spinner label="Loading history…" />

  if (!entries || entries.length === 0) {
    return (
      <EmptyState title="No changes recorded">
        Every change made through this UI is appended to the audit table. Rows created
        before it existed have no history here.
      </EmptyState>
    )
  }

  return (
    <ul className="history">
      {entries.map((entry) => {
        const changes = diff(schema, entry)
        return (
          <li key={entry.id} className="history__item">
            <div className="history__meta">
              <span className="history__who">{shortenUpn(entry.changed_by)}</span>
              <span>{entryVerb(entry)}</span>
              <span>·</span>
              <span>{formatTimestamp(entry.changed_at)}</span>
            </div>
            {changes.length === 0 ? (
              <p className="field__help" style={{ margin: 0 }}>
                No field values changed.
              </p>
            ) : (
              <dl className="diff">
                {changes.map((change) => (
                  <div key={change.field} style={{ display: 'contents' }}>
                    <dt className="diff__field">{change.label}</dt>
                    <dd className="diff__values">
                      <span className="diff__old">
                        <CellValue field={change.schema} value={change.from} />
                      </span>
                      <span className="diff__arrow" aria-label="became">
                        →
                      </span>
                      <span className="diff__new">
                        <CellValue field={change.schema} value={change.to} />
                      </span>
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        )
      })}
    </ul>
  )
}
